import { useCallback } from 'react';
import type { NavigateOptions } from 'react-router';
import { useSearchParams } from 'react-router';

export const useTypedSearchParams = <T extends Record<string, any>>() => {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback(
    <K extends keyof T>(key: K, defaultValue?: string) => {
      const value = searchParams.get(key as string);
      return (value as Nullish<T[K]>) ?? defaultValue ?? value;
    },
    [searchParams],
  );

  const getAll = useCallback(
    <K extends keyof T>(key: K, defaultValue?: string[]) => {
      const values = searchParams.getAll(key as string);
      return (values as T[K]) ?? defaultValue ?? values;
    },
    [searchParams],
  );

  const set = useCallback(
    <K extends keyof T>(key: K, value: string) => {
      searchParams.set(key as string, value);
    },
    [searchParams],
  );

  const append = useCallback(
    <K extends keyof T>(key: K, value: string) => {
      searchParams.append(key as string, value);
    },
    [searchParams],
  );

  const remove = useCallback(
    <K extends keyof T>(key: K) => {
      searchParams.delete(key as string);
    },
    [searchParams],
  );

  const flush = useCallback(
    (options?: NavigateOptions) => {
      setSearchParams(searchParams, options);
    },
    [searchParams, setSearchParams],
  );

  return { get, getAll, set, flush, remove, append };
};
